import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Label, LabelsByBoards } from '../../../../../../../common/labels/labelTypes';
import { fetchBoards } from '../../../../../../element/board/boardService';
import {
    selectBoardsToFetchForCurrentPath,
    selectCurrentPathLabels,
} from '../../../../../../element/labels/labelSelectors';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import { getCurrentUserId } from '../../../../../../user/currentUserSelector';
import { mergeSuggestedLabels } from '../utils/labelListMergeUtils';
import { createRecentLabelsSection } from '../utils/labelPopupUtils';

type Props = {
    allLabels: Label[];
    searchString: string;
};

type UseLabelListDataReturnType = {
    allLabelsFiltered: Label[];
    suggestedLabels: LabelsByBoards;
    hasVisibleLabels: boolean;
    isSearching: boolean;
};

const useLabelListData = ({ allLabels, searchString }: Props): UseLabelListDataReturnType => {
    const dispatch = useDispatch();
    const { toFetch, isBoardFetchInProgress } = useSelector(selectBoardsToFetchForCurrentPath);
    const currentPathLabels = useSelector(selectCurrentPathLabels);
    const currentUserId = useSelector(getCurrentUserId);
    const [suggestedLabels, setSuggestedLabels] = useState<LabelsByBoards>(currentPathLabels);
    const translate = useTranslator('popup', 'labels.menu');

    const normalizedSearch = searchString.trim().toLowerCase();
    const isSearching = Boolean(normalizedSearch);

    useEffect(() => {
        if (!toFetch.length) return;

        dispatch(fetchBoards({ boardIds: toFetch }));
    }, [dispatch, toFetch]);

    useEffect(() => {
        if (isBoardFetchInProgress) {
            return;
        }

        setSuggestedLabels((prevSuggestedLabels) => {
            if (prevSuggestedLabels.length) {
                const mergedSuggestedLabels = mergeSuggestedLabels(prevSuggestedLabels, currentPathLabels, allLabels);
                if (mergedSuggestedLabels.length) return mergedSuggestedLabels;
            }

            if (currentPathLabels.length) return currentPathLabels;

            return createRecentLabelsSection(allLabels, currentUserId, translate('recentlyCreated'));
        });
    }, [allLabels, currentPathLabels, currentUserId, isBoardFetchInProgress]);

    const allLabelsFiltered = useMemo(
        () => allLabels.filter((label) => !isSearching || label.name.toLowerCase().includes(normalizedSearch)),
        [allLabels, isSearching, normalizedSearch],
    );

    const hasVisibleLabels = isSearching ? allLabelsFiltered.length > 0 : suggestedLabels.length > 0;

    return {
        allLabelsFiltered,
        suggestedLabels,
        hasVisibleLabels,
        isSearching,
    };
};

export default useLabelListData;
